import { gql } from '@apollo/client';

/**
 * The offline review family (`backend/app/graphql_endpoints/offline_research/reviews.py`).
 *
 * These are *stored artifacts*, not computations: an agent runs every night and
 * writes the review, and every one of these queries reads the latest one back.
 * Nothing here is on a request path, so a screen can ask for a whole review
 * without paying for it — and nothing here is guaranteed to exist, because a
 * subject the agents have never covered simply has no artifact yet.
 *
 * All of them require a session (`@requireAuth`); an anonymous caller gets a
 * 401 on the field rather than an empty result, which is why every hook here
 * uses `errorPolicy: 'all'` and treats "no review" as a state rather than a
 * failure.
 *
 * `window` is a cadence — daily | weekly | monthly | quarterly | yearly — and
 * each query documents its own default, because a fund's PM review is written
 * monthly while the market view is written every morning.
 */

/**
 * One review rendered as screen panels.
 *
 * This is the shape the whole review family collapses to: a block per panel,
 * each carrying the verdict, what is working, what is not, what to watch, the
 * findings behind it and the actions it asks for. Screens render this and never
 * parse the raw artifact JSON.
 */
const COMMENTARY = `
  screen
  headline
  summary
  prose
  verdict
  working
  notWorking
  watch
  criticStatus
  asOf
  window
  reviewId
  findings {
    id
    title
    summary
    prose
    explanation
    verdict
    horizon
    metric
    thresholdUsed
    graphicRef
    actionItemIds
  }
  actions {
    actionItemId
    title
    description
    category
    priority
    owner
    urgency
    cheapestRoute
    cost
    pending
    findingId
    whyFeatured
  }
  graphics
`;

/**
 * The fund manager's own review of one scheme, as panels: performance,
 * attribution, strategy adherence, compliance, style drift, trading behaviour,
 * risk, concentration, costs, peers, actions.
 *
 * This is the per-fund half of "what is helping and what is not" — written from
 * the manager's seat rather than an investor's, which is why it carries
 * mandate and compliance panels an investor review never would.
 */
export const MF_PM_REVIEW_COMMENTS = gql`
  query MfPmReviewComments($securitySymbol: String!, $window: String! = "monthly") {
    getMfPmReviewComments(securitySymbol: $securitySymbol, window: $window) { ${COMMENTARY} }
  }
`;

/** The same review's headline and stored artifact, for the brief above the panels. */
export const MF_PM_REVIEW = gql`
  query MfPmReview($securitySymbol: String!, $window: String! = "monthly") {
    getMfPmReview(securitySymbol: $securitySymbol, window: $window) {
      kind
      window
      brief
      review
    }
  }
`;

/**
 * One asset-class model's book review at a cadence: its objective, tracking
 * error, follower dispersion, book concentration and approved-list candidates.
 *
 * The artifact only — the review agents publish no per-screen commentary for a
 * model, so the headline, the horizon read and the findings are all read out
 * of `review` (JSON). `modelId` is the register's integer id.
 */
export const MODEL_REVIEW = gql`
  query ModelReview($modelId: Int!, $window: String! = "monthly") {
    getModelReview(modelId: $modelId, window: $window) {
      kind
      window
      brief
      review
    }
  }
`;

/**
 * A manager's whole book: every fund they run placed as doing well, not doing
 * well, at risk or needing action, with the action items per fund.
 *
 * Keyed by `managerName` as the scheme master spells it — there is no user
 * account behind a fund manager, so the name from `getMetaMutualFund.managers`
 * is the join key.
 */
export const FUND_MANAGER_REVIEW = gql`
  query FundManagerReview($managerName: String!, $window: String! = "monthly") {
    getFundManagerReview(managerName: $managerName, window: $window) {
      kind
      window
      brief
      review
    }
  }
`;

/** The manager's book review as panels — the dashboard half of the same artifact. */
export const FUND_MANAGER_COMMENTS = gql`
  query FundManagerComments($managerName: String!, $window: String! = "monthly") {
    getFundManagerComments(managerName: $managerName, window: $window) { ${COMMENTARY} }
  }
`;

/**
 * What every review in the caller's book says about one fund.
 *
 * Distinct from the PM review above: this is the fund seen from the outside —
 * the fund's own review plus each finding in a client portfolio review that
 * names this scheme.
 */
export const FUND_REVIEW_COMMENTARY = gql`
  query FundReviewCommentary($securitySymbol: String!, $screens: [String!]) {
    getFundReviewCommentary(securitySymbol: $securitySymbol, screens: $screens) { ${COMMENTARY} }
  }
`;

/**
 * The four-P grade card: organization, people, process and performance graded
 * A–D with the verdict and the action it implies.
 */
export const MF_RESEARCH_GRADE = gql`
  query MfResearchGrade($securitySymbol: String!, $window: String) {
    getMfResearchGrade(securitySymbol: $securitySymbol, window: $window) {
      securitySymbol
      overallGrade
      overallScore
      verdict
      action
      confidence
      coverage
      processGrade
      performanceGrade
      peopleGrade
      organizationGrade
    }
  }
`;

/** The four-P research as panels, one per area, for the fund's research tab. */
export const MF_RESEARCH_COMMENTS = gql`
  query MfResearchComments($securitySymbol: String!, $window: String) {
    getMfResearchComments(securitySymbol: $securitySymbol, window: $window) { ${COMMENTARY} }
  }
`;

/**
 * The house market view for a cadence — what moved, what is helping and what is
 * not, the outlook, the events. Written once each morning by the market
 * strategist and shared by every review and agent, so the desk and the client
 * screens quote the same thing.
 */
export const MARKET_COMMENTS = gql`
  query MarketComments($window: String! = "daily") {
    getMarketComments(window: $window) { ${COMMENTARY} }
  }
`;

/** Open review actions with owner, urgency, route, cost, age and delay cost. */
export const REVIEW_OPEN_ACTIONS = gql`
  query ReviewOpenActions {
    getReviewOpenActions
  }
`;

/* ── the distributor's side: the book, each client, each portfolio, each goal ── */

/**
 * The calling distributor's book review. Weekly is the call list; daily,
 * quarterly and yearly are the dashboard note — what is helping clients, what
 * is not, what to do. Resolved from the advisor profile behind the token, so
 * it takes no id; a caller who is not a distributor gets null.
 */
export const ADVISOR_BOOK_REVIEW = gql`
  query AdvisorBookReview($window: String! = "weekly") {
    getAdvisorBookReview(window: $window) {
      kind
      window
      brief
      review
    }
  }
`;

export const ADVISOR_BOOK_COMMENTS = gql`
  query AdvisorBookComments($window: String! = "quarterly") {
    getAdvisorBookComments(window: $window) { ${COMMENTARY} }
  }
`;

/**
 * The client roster from the latest book review: every client placed as
 * doing_well | not_doing_well | at_risk | needs_action, with the reasons, their
 * open actions across every cadence, and the suggestions to improve.
 */
export const ADVISOR_BOOK_CLIENTS = gql`
  query AdvisorBookClients($window: String! = "monthly", $status: String) {
    getAdvisorBookClients(window: $window, status: $status) {
      clientId
      clientName
      status
      onTrack
      assetsInr
      returnPct
      changeInr
      openActions
      reasons
      actionItems {
        cadence source title owner urgency status ageDays pending cheapestRoute cost actionItemId findingId
      }
      suggestions { title cheapestRoute cost owner window }
    }
  }
`;

/**
 * One client's synthesis across every goal and portfolio. Resolves the client
 * from the token — an advisor reads a client's by sending `X-Target-User-Id`
 * (see `clientScope.asClient`), the same way goals are read on their behalf.
 */
export const CLIENT_REVIEW = gql`
  query ClientReview($window: String! = "daily") {
    getClientReview(window: $window) {
      kind
      window
      brief
      review
    }
  }
`;

/** The latest stored review of one goal portfolio (headline and artifact). */
export const PORTFOLIO_REVIEW = gql`
  query PortfolioReview($portfolioSecuritySymbol: String!, $window: String! = "daily") {
    getPortfolioReview(portfolioSecuritySymbol: $portfolioSecuritySymbol, window: $window) {
      kind
      window
      brief
      review
    }
  }
`;

/**
 * What the latest review says beside each screen of one portfolio. `screens`
 * picks from market_impact, performance, attribution, risk, holdings,
 * allocation, goal_progress, costs, suitability, activity, compliance;
 * `holdingSymbol` keeps only the findings about that holding.
 */
export const REVIEW_COMMENTARY = gql`
  query ReviewCommentary(
    $portfolioSecuritySymbol: String!
    $screens: [String!]
    $holdingSymbol: String
    $window: String
  ) {
    getReviewCommentary(
      portfolioSecuritySymbol: $portfolioSecuritySymbol
      screens: $screens
      holdingSymbol: $holdingSymbol
      window: $window
    ) { ${COMMENTARY} }
  }
`;

/** A goal's page: goal progress, allocation, suitability and activity slices of its portfolios' reviews. */
export const GOAL_REVIEW_COMMENTARY = gql`
  query GoalReviewCommentary($goalId: Int!, $screens: [String!]) {
    getGoalReviewCommentary(goalId: $goalId, screens: $screens) { ${COMMENTARY} }
  }
`;

/**
 * Every past edition of a portfolio's review, newest first, with the stored
 * PDF links — the client edition and the executive (advisor) edition.
 */
export const REVIEW_HISTORY = gql`
  query ReviewHistory($portfolioSecuritySymbol: String!, $window: String, $limit: Int! = 24) {
    getReviewHistory(portfolioSecuritySymbol: $portfolioSecuritySymbol, window: $window, limit: $limit) {
      reviewId
      kind
      window
      subject
      asOf
      generatedAt
      headline
      brief
      overallVerdict
      criticStatus
      isLatest
      isClean
      pdfUrl
      pdfDownloadUrl
      pdfCseUrl
      pdfCseDownloadUrl
      pdfGeneratedAt
    }
  }
`;

/** A fresh, time-limited download link for one stored review PDF. */
export const REVIEW_REPORT_URL = gql`
  query ReviewReportUrl($reviewId: Int!, $edition: String! = "client") {
    getReviewReportUrl(reviewId: $reviewId, edition: $edition)
  }
`;

/* Inlined rather than a fragment: the cache runs without __typename
   (addTypename: false), so it cannot match a fragment's type condition and
   reads the fields back as missing — the note never reached the screen. */
const MF_REPORT_NOTE = `
    securitySymbol
    reviewId
    window
    asOf
    headline
    verdict { call grade }
    reportUrl
    reportCseUrl
    criticStatus
`;

/**
 * The MF research agent's latest note as a REPORT — the call and grade, the
 * critic's status and its two PDFs (`reportUrl`, and the `reportCseUrl`
 * edition). What the fund's Reports tab opens, the way the stock page opens
 * `getStockResearchNote`; `reviewId` is how a queued run is seen to land.
 * Read once for the latest and once per window, so the editions dropdown has
 * each window's latest even where the review history has nothing.
 */
export const MF_RESEARCH_REPORT = gql`
  query MfResearchReport($securitySymbol: String!) {
    latest: getMfResearchNote(securitySymbol: $securitySymbol) { ${MF_REPORT_NOTE} }
    quarterly: getMfResearchNote(securitySymbol: $securitySymbol, window: "quarterly") { ${MF_REPORT_NOTE} }
    monthly: getMfResearchNote(securitySymbol: $securitySymbol, window: "monthly") { ${MF_REPORT_NOTE} }
    yearly: getMfResearchNote(securitySymbol: $securitySymbol, window: "yearly") { ${MF_REPORT_NOTE} }
  }
`;

/**
 * The analyst's note on one scheme, in the two shapes a screen reads it in.
 *
 * The horizon read is one sentence each for the short, medium and long horizon
 * (a year, three years, five to ten), with the edition record beside it —
 * whether this is the quarterly full edition or a monthly update on it.
 *
 * `whatsWorking` / `whatsNotWorking` are the analyst's own lines about the
 * fund, not a per-screen panel: they judge the scheme, where
 * `ReviewCommentaryType.working` judges one area of it. So they read as the
 * standing view a period's numbers are measured against, and a screen showing
 * both has to say which is which.
 */
export const MF_RESEARCH_NOTE = gql`
  query MfResearchNote($securitySymbol: String!, $window: String) {
    getMfResearchNote(securitySymbol: $securitySymbol, window: $window) {
      securitySymbol
      reviewId
      window
      asOf
      headline
      edition
      horizonSummaries
      whatsWorking
      whatsNotWorking
    }
  }
`;
